import { prelaunchUploadImage } from '../../api/imageApi'
import { getTagsByContext } from '../../api/tagsApi'
import { difficultLevelOptions, LocaleOptions } from '../../helpers/constant'
import { mergeFormListByKey } from '../../helpers/form-list'
import { getImageFileName } from '../../helpers/image-upload-helper'
import translate from '../../i18n'
import {
  lessonDescriptionsTargetUserGroupsEnOptions,
  lessonDescriptionsTargetUserGroupsZhCNOptions,
} from '../../pages/card-stacks-container/constant'
import { PrelaunchLessonDescriptionFormValues } from '../../pages/prelaunch-organization-forms-container/share-types'
import { TaggingContextEnums } from '../../pages/tags-container/constant'
import { ITag, ITagsWithCategory } from '../../pages/tags-container/share-type'
import { request } from '../../request'
import { LocaleType, SupportedPrelaunchUploadImageTypeEnums } from '../../share-types'
import { MinusCircleOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons'
import { Button, Divider, Form, InputNumber, Radio, Select, Space, Upload, UploadFile } from 'antd'
import TextArea from 'antd/lib/input/TextArea'
import { useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'

const { Option } = Select

const LessonDescriptionForm = ({
  onFinish,
  formData,
  organizationUniqueId,
  isEdit,
  isPublicEdit = false,
}: {
  onFinish: (values: PrelaunchLessonDescriptionFormValues) => void
  formData?: PrelaunchLessonDescriptionFormValues
  organizationUniqueId: string
  isEdit: boolean
  isPublicEdit?: boolean
}) => {
  const [form] = Form.useForm()
  const [activitiesTags, setActivitiesTags] = useState<ITag[]>([])
  const [labelsTags, setLabelsTags] = useState<ITag[]>([])
  const [lessonDescriptionsTargetUserGroupsOptions] = useState<{ [key: string]: any }>({
    [LocaleType.zh_CN]: lessonDescriptionsTargetUserGroupsZhCNOptions,
    [LocaleType.en]: lessonDescriptionsTargetUserGroupsEnOptions,
  })
  const translationsValues = Form.useWatch('translations', form)
  const isUpdate = isEdit || isPublicEdit

  useEffect(() => {
    if (formData) {
      form.setFieldsValue(formData)
    }
  }, [formData])

  useEffect(() => {
    const promises = [
      getTagsByContext(TaggingContextEnums.ACTIVITIES),
      getTagsByContext(TaggingContextEnums.LABELS),
    ]
    Promise.all(promises).then(([activities, labels]) => {
      if (activities) {
        setActivitiesTags(flatTagsWithContext(activities))
      }
      if (labels) {
        setLabelsTags(flatTagsWithContext(labels))
      }
    })
  }, [])

  const flatTagsWithContext = (res: ITagsWithCategory): ITag[] => {
    return Object.values(res).flat()
  }

  const getTagsOptions = (tags: ITag[], taggingContex: TaggingContextEnums) => {
    return tags.map(tag => {
      const categoryAndSubCategoryLabel = [tag.category, tag.subCategory]
        .filter(item => item)
        .join('/')
      const tagName = tag.tagTranslations.find(t => t.locale === LocaleType.zh_CN)?.name
      if (taggingContex === TaggingContextEnums.ACTIVITIES) {
        return {
          label: `${tagName} (${categoryAndSubCategoryLabel})`,
          value: tag.id,
        }
      }

      return {
        label: `${tagName}`,
        value: tag.id,
      }
    })
  }

  const normFile = (e: any) => {
    if (Array.isArray(e)) {
      return e
    }
    return e?.fileList
  }

  const uploadUrl = (options: any, type: SupportedPrelaunchUploadImageTypeEnums) => {
    const { onSuccess, onError, file, filename } = options
    const fileExtension = file.name.split('.').pop()
    const imageFilename = `${filename}_${file.uid}.${fileExtension}`
    prelaunchUploadImage(organizationUniqueId, imageFilename, type)
      .then(res => {
        onSuccess(res)
        request({
          url: res,
          method: 'PUT',
          data: file,
          headers: {
            'x-amz-acl': 'public-read',
          },
        })
      })
      .catch(err => {
        onError({ err })
      })
  }

  const getOptionsByLocale = (index: number) => {
    if (!translationsValues) {
      return lessonDescriptionsTargetUserGroupsZhCNOptions
    }
    const locale = translationsValues[index]?.locale
    if (!locale) {
      return lessonDescriptionsTargetUserGroupsZhCNOptions
    }
    return lessonDescriptionsTargetUserGroupsOptions[locale]
  }

  const onSubimtForms = (values: PrelaunchLessonDescriptionFormValues) => {
    const { photos, credits, startBookable, lateBefore, labelList, translations, ...others } =
      values

    const data = {
      ...others,
      translations: mergeFormListByKey(translations, formData?.translations, 'locale'),
      credits: isUpdate ? credits : 0,
      startBookable: isUpdate ? startBookable : 10,
      lateBefore: isUpdate ? lateBefore : 12,
      labelList: isUpdate ? labelList : [],
      photoFileNames: photos
        ?.map((photo: UploadFile) => getImageFileName('photos', [photo]))
        .filter(item => item),
    }
    onFinish(data)
  }

  return (
    <Form
      name="lesson-description-form"
      form={form}
      labelCol={{ span: 6 }}
      wrapperCol={{ span: 12 }}
      onFinish={onSubimtForms}
      initialValues={{
        isAllDay: false,
        activityList: [],
      }}
    >
      {isPublicEdit && (
        <>
          <Form.Item name="credits" hidden>
            <InputNumber />
          </Form.Item>
          <Form.Item name="labelList" hidden>
            <Select mode="multiple" />
          </Form.Item>
          <Form.Item name="startBookable" hidden>
            <InputNumber />
          </Form.Item>
          <Form.Item name="lateBefore" hidden>
            <InputNumber />
          </Form.Item>
        </>
      )}
      <Form.Item label={translate('lessonDescription.duration')} required>
        <Space.Compact>
          <Form.Item
            name="duration"
            noStyle
            rules={[{ required: true, message: translate('lessonDescription.duration.message') }]}
          >
            <InputNumber />
          </Form.Item>
          <div className="addon-after">分钟</div>
        </Space.Compact>
      </Form.Item>

      {isEdit && (
        <Form.Item label={translate('lessonDescription.credits')} required>
          <Space.Compact>
            <Form.Item
              name="credits"
              noStyle
              rules={[{ required: true, message: translate('lessonDescription.credits.message') }]}
            >
              <InputNumber />
            </Form.Item>
            <div className="addon-after">点数</div>
          </Space.Compact>
        </Form.Item>
      )}

      <Divider style={{ fontSize: '14px' }}>{translate('lessonInformation')}</Divider>

      <Form.List
        name="translations"
        initialValue={[
          {
            locale: LocaleType.zh_CN,
            checkInNotes:
              '我们温馨提醒您，请提前X分钟抵达场馆，若您迟到，将不被允许进入，感谢您的理解与支持！',
          },
        ]}
      >
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name, ...restField }, index) => (
              <div key={key}>
                <Form.Item
                  {...restField}
                  label={translate('locale')}
                  name={[name, 'locale']}
                  rules={[{ required: true, message: '请选择语言！' }]}
                >
                  <Select disabled={!isEdit}>
                    {LocaleOptions.map(item => (
                      <Option key={item.value} value={item.value}>
                        {item.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  label={translate('name')}
                  rules={[{ required: true, message: translate('name.message') }]}
                  {...restField}
                  name={[name, 'name']}
                >
                  <TextArea showCount />
                </Form.Item>
                <Form.Item
                  label={translate('lessonDescriptionTranslations.description')}
                  {...restField}
                  name={[name, 'description']}
                  rules={[
                    { required: true, message: translate('lessonDescription.description.message') },
                    {
                      validator: (_rule: unknown, value?: string) =>
                        (value?.trim().length ?? 0) >= 20
                          ? Promise.resolve()
                          : Promise.reject(new Error('课程描述至少需要20个字')),
                    },
                  ]}
                  extra="如您的课程是瑜伽普拉提，请提供课程种类的细分，ex：哈他瑜伽、阴瑜伽、普拉提器械核心强化、普拉提器械肩背塑形"
                >
                  <TextArea showCount />
                </Form.Item>

                <Form.Item
                  label={translate('lessonDescriptionTranslations.highlights')}
                  {...restField}
                  name={[name, 'highlights']}
                >
                  <TextArea showCount />
                </Form.Item>
                <Form.Item
                  label={translate('lessonDescriptionTranslations.specialNoteToUsers')}
                  {...restField}
                  name={[name, 'specialNoteToUsers']}
                  extra={
                    <>
                      <div>如果是球类或是需要特殊装备的课程，请备注是否有提供、或是需要自备:</div>
                      <div>
                        1. 有提供球拍/道服/攀岩鞋等装备; 如果没有，请备注是否有租赁服务以及费用
                      </div>
                      <div>2. 请您穿防滑袜、瑜伽服、自备水杯毛巾，等等</div>
                    </>
                  }
                >
                  <TextArea showCount />
                </Form.Item>

                <Form.Item
                  label={translate('lessonDescriptionTranslations.checkInNotes')}
                  {...restField}
                  name={[name, 'checkInNotes']}
                >
                  <TextArea showCount />
                </Form.Item>

                <Form.Item
                  label={translate('lessonDescriptionTranslations.targetUserGroups')}
                  {...restField}
                  name={[name, 'targetUserGroups']}
                >
                  <Select allowClear options={getOptionsByLocale(index)}></Select>
                </Form.Item>

                {isEdit && (
                  <>
                    <Form.Item label={translate('funFact')} {...restField} name={[name, 'funFact']}>
                      <TextArea showCount />
                    </Form.Item>

                    <Form.Item
                      label={translate('lessonDescriptionTranslations.lessonPrerequisites')}
                      {...restField}
                      name={[name, 'lessonPrerequisites']}
                    >
                      <TextArea showCount />
                    </Form.Item>

                    <Form.Item
                      label={translate('lessonDescriptionTranslations.otherNotes')}
                      {...restField}
                      name={[name, 'otherNotes']}
                    >
                      <TextArea showCount />
                    </Form.Item>
                  </>
                )}

                {fields.length > 1 ? (
                  <Form.Item colon={false} label="  ">
                    <MinusCircleOutlined
                      className="dynamic-delete-button"
                      onClick={() => remove(name)}
                    />
                  </Form.Item>
                ) : null}
              </div>
            ))}

            {isEdit && fields.length < Object.keys(LocaleType).length && (
              <Form.Item colon={false} label="  ">
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  {translate('addNewTranslation')}
                </Button>
              </Form.Item>
            )}
          </>
        )}
      </Form.List>

      <Divider style={{ fontSize: '14px' }}>{translate('studio.taggings')}</Divider>

      <Form.Item name="activityList" label={translate('activityList')}>
        <Select
          mode="multiple"
          allowClear
          style={{ width: '100%' }}
          placeholder={translate('activities.placeholder')}
          options={getTagsOptions(activitiesTags, TaggingContextEnums.ACTIVITIES)}
        />
      </Form.Item>

      {isEdit && (
        <Form.Item name="labelList" label={translate('label')}>
          <Select
            mode="multiple"
            allowClear
            style={{ width: '100%' }}
            placeholder={translate('labels.placeholder')}
            options={getTagsOptions(labelsTags, TaggingContextEnums.LABELS)}
          />
        </Form.Item>
      )}

      <Divider style={{ fontSize: '14px' }}>{translate('bookingRules')}</Divider>

      {isEdit && (
        <Form.Item
          label={translate('lessonDescription.startBookable')}
          required
          extra={translate('lessonDescription.startBookable.tips')}
        >
          <Space.Compact>
            <Form.Item
              name="startBookable"
              noStyle
              rules={[
                { required: true, message: translate('lessonDescription.startBookable.message') },
              ]}
            >
              <InputNumber />
            </Form.Item>
            <div className="addon-after">天</div>
          </Space.Compact>
        </Form.Item>
      )}

      <Form.Item
        label={translate('lessonDescription.bookBefore')}
        required
        extra={translate('lessonDescription.bookBefore.tips')}
      >
        <Space.Compact>
          <Form.Item
            name="bookBefore"
            noStyle
            rules={[{ required: true, message: translate('lessonDescription.bookBefore.message') }]}
          >
            <InputNumber />
          </Form.Item>
          <div className="addon-after">分钟</div>
        </Space.Compact>
      </Form.Item>

      {isEdit && (
        <Form.Item
          label={translate('lessonDescription.lateBefore')}
          required
          extra={translate('lessonDescription.lateBefore.tips')}
        >
          <Space.Compact>
            <Form.Item
              name="lateBefore"
              noStyle
              rules={[
                { required: true, message: translate('lessonDescription.lateBefore.message') },
              ]}
            >
              <InputNumber />
            </Form.Item>
            <div className="addon-after">小时</div>
          </Space.Compact>
        </Form.Item>
      )}

      <Divider style={{ fontSize: '14px' }}>{translate('lessonInformation')}</Divider>

      <Form.Item
        name="maxCapacity"
        label={translate('lessonDescription.maxCapacity')}
        rules={[{ required: true, message: translate('lessonDescription.maxCapacity.message') }]}
      >
        <InputNumber />
      </Form.Item>

      <Form.Item
        name="difficultLevel"
        label={translate('lessonDescription.difficultLevel')}
        rules={[{ required: true, message: translate('lessonDescription.difficultLevel.message') }]}
      >
        <Select>
          {difficultLevelOptions.map(item => (
            <Select.Option key={item.value} value={item.value}>
              {item.label}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item
        name="isAllDay"
        label={translate('lessonDescription.isAllDay')}
        rules={[{ required: true, message: translate('lessonDescription.isAllDay.message') }]}
      >
        <Radio.Group>
          <Radio value={true}>是</Radio>
          <Radio value={false}>否</Radio>
        </Radio.Group>
      </Form.Item>

      <Form.Item
        name="photos"
        label={translate('photos')}
        valuePropName="fileList"
        getValueFromEvent={normFile}
        extra="建议图片尺寸：9：16或者4：3"
      >
        <Upload
          name="photos"
          customRequest={config =>
            uploadUrl(config, SupportedPrelaunchUploadImageTypeEnums.LESSON_DESCRIPTION_PHOTOS)
          }
          listType="picture"
          multiple
        >
          <Button icon={<UploadOutlined />}>点击上传</Button>
        </Upload>
      </Form.Item>

      <Form.Item wrapperCol={{ sm: { offset: 8, span: 16 } }}>
        <Button htmlType="submit" type="primary">
          <FormattedMessage id="submit"></FormattedMessage>
        </Button>
      </Form.Item>
    </Form>
  )
}

export default LessonDescriptionForm
